import React, { useEffect, useState } from 'react';
import { Trash2, User, Edit2, ChevronRight, DollarSign, Users, X } from 'lucide-react';

const AdminCreators = () => {
    const [creators, setCreators] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedCreator, setSelectedCreator] = useState(null);
    const [referrals, setReferrals] = useState(null);
    const [loadingReferrals, setLoadingReferrals] = useState(false);

    useEffect(() => {
        fetchCreators();
    }, []);

    const fetchCreators = async () => {
        const token = localStorage.getItem('paradise_token');
        const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:4000';
        try {
            const res = await fetch(`${API_BASE}/api/admin/creators`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                setCreators(await res.json());
            } else {
                throw new Error(`HTTP Error: ${res.status} ${res.statusText}`);
            }
        } catch (e) {
            console.error(e);
            setError(e.message || 'Failed to fetch creators');
        } finally {
            setLoading(false);
        }
    };

    const fetchReferrals = async (id) => {
        setLoadingReferrals(true);
        const token = localStorage.getItem('paradise_token');
        const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:4000';
        try {
            const res = await fetch(`${API_BASE}/api/admin/creators/${id}/referrals`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                setReferrals(await res.json());
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoadingReferrals(false);
        }
    };

    const handleSelectCreator = (creator) => {
        setSelectedCreator(creator);
        setReferrals(null);
        fetchReferrals(creator._id);
    };

    const handleDelete = async (id, name) => {
        if (!window.confirm(`Are you sure you want to PERMANENTLY DELETE "${name}" and all their photo sets? This action cannot be undone.`)) return;

        const token = localStorage.getItem('paradise_token');
        const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:4000';
        try {
            const res = await fetch(`${API_BASE}/api/admin/creators/${id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (res.ok) {
                if (selectedCreator?._id === id) setSelectedCreator(null);
                fetchCreators();
            } else {
                const data = await res.json();
                alert(data.message || "Failed to delete creator");
            }
        } catch (e) {
            console.error(e);
            alert("Error deleting creator");
        }
    };

    const handleUpdateChannel = async (e, id, currentNum) => {
        e.stopPropagation();
        const newNum = window.prompt(`Enter new Channel Number for this creator (currently ${currentNum || 'None'}):`, currentNum || '');
        if (newNum === null) return; // Cancelled

        const channelNumber = parseInt(newNum);
        if (isNaN(channelNumber) && newNum !== '') {
            alert("Please enter a valid number");
            return;
        }

        const token = localStorage.getItem('paradise_token');
        const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:4000';
        try {
            const res = await fetch(`${API_BASE}/api/admin/creators/${id}`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ channelNumber: newNum === '' ? null : channelNumber })
            });

            if (res.ok) {
                fetchCreators();
                if (selectedCreator?._id === id) {
                    setSelectedCreator({ ...selectedCreator, channelNumber: newNum === '' ? null : channelNumber });
                }
            } else {
                alert("Failed to update channel");
            }
        } catch (e) {
            console.error(e);
            alert("Error updating channel");
        }
    };

    return (
        <div className="relative">
            <h2 className="text-2xl font-bold mb-6 italic uppercase tracking-widest font-mono">Manage Creators</h2>

            <div className="flex flex-col lg:flex-row gap-8">
                {/* Creators List */}
                <div className={`flex-1 transition-all duration-300 ${selectedCreator ? 'lg:w-2/3' : 'w-full'}`}>
                    {loading ? (
                        <div className="text-gray-500 font-mono text-xs animate-pulse">Scanning database...</div>
                    ) : creators.length === 0 ? (
                        <div className="text-gray-400 font-mono text-xs italic">No creators found in database.</div>
                    ) : (
                        <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-gray-50 border-b border-gray-200 text-[10px] font-bold uppercase tracking-wider text-gray-400">
                                        <th className="p-4">Profile</th>
                                        <th className="p-4">Type</th>
                                        <th className="p-4">Channel</th>
                                        <th className="p-4 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {creators.map(creator => (
                                        <tr
                                            key={creator._id}
                                            onClick={() => handleSelectCreator(creator)}
                                            className={`border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer ${selectedCreator?._id === creator._id ? 'bg-zinc-50 border-l-4 border-l-black' : ''}`}
                                        >
                                            <td className="p-4">
                                                <div className="flex items-center space-x-3">
                                                    <div className="w-10 h-10 rounded-full bg-gray-100 border border-gray-200 overflow-hidden flex items-center justify-center">
                                                        {creator.profileImage?.variants?.[0] ? (
                                                            <img
                                                                src={creator.profileImage.variants[0]}
                                                                alt={creator.name}
                                                                className="w-full h-full object-cover"
                                                            />
                                                        ) : (
                                                            <User size={18} className="text-gray-300" />
                                                        )}
                                                    </div>
                                                    <div>
                                                        <div className="text-sm font-bold text-gray-800 uppercase tracking-tight">{creator.name}</div>
                                                        <div className="text-[10px] text-gray-400 font-mono">{creator.email}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="p-4">
                                                <span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-full border ${creator._type === 'creator'
                                                    ? 'bg-blue-50 text-blue-600 border-blue-100'
                                                    : 'bg-amber-50 text-amber-600 border-amber-100'
                                                    }`}>
                                                    {creator._type}
                                                </span>
                                            </td>
                                            <td className="p-4">
                                                <button
                                                    onClick={(e) => handleUpdateChannel(e, creator._id, creator.channelNumber)}
                                                    className="flex items-center space-x-2 text-xs font-mono group"
                                                >
                                                    <span className={`px-2 py-1 rounded ${creator.channelNumber ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-400 italic'}`}>
                                                        {creator.channelNumber ? `CH ${creator.channelNumber}` : 'Unassigned'}
                                                    </span>
                                                    <Edit2 size={12} className="text-gray-300 group-hover:text-blue-500" />
                                                </button>
                                            </td>
                                            <td className="p-4 text-right">
                                                <div className="flex items-center justify-end space-x-2">
                                                    <ChevronRight size={16} className={`text-gray-300 transition-transform ${selectedCreator?._id === creator._id ? 'rotate-180 text-black' : ''}`} />
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Detailed Sidebar */}
                {selectedCreator && (
                    <div className="lg:w-1/3 bg-white rounded-lg shadow-lg border border-gray-100 overflow-hidden h-fit sticky top-8 animate-in slide-in-from-right duration-300">
                        <div className="bg-black p-4 flex justify-between items-center text-white">
                            <h3 className="text-xs font-bold uppercase tracking-widest font-mono">Creator Details</h3>
                            <button onClick={() => setSelectedCreator(null)} className="hover:rotate-90 transition-transform">
                                <X size={16} />
                            </button>
                        </div>

                        <div className="p-6 space-y-8">
                            {/* Profile Info */}
                            <div className="text-center">
                                <div className="w-24 h-24 rounded-full mx-auto bg-gray-100 border-4 border-gray-50 shadow-inner overflow-hidden mb-4">
                                    {selectedCreator.profileImage?.variants?.[0] ? (
                                        <img
                                            src={selectedCreator.profileImage.variants[0]}
                                            alt={selectedCreator.name}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center">
                                            <User size={40} className="text-gray-300" />
                                        </div>
                                    )}
                                </div>
                                <h4 className="text-lg font-bold text-gray-900 uppercase tracking-tight">{selectedCreator.name}</h4>
                                <p className="text-xs text-gray-400 font-mono mb-4">{selectedCreator.email}</p>

                                <div className="flex justify-center space-x-2">
                                    <span className="px-3 py-1 bg-gray-100 rounded-full text-[9px] font-bold uppercase tracking-widest text-gray-500 border border-gray-200">
                                        {selectedCreator._type}
                                    </span>
                                    <span className={`px-3 py-1 rounded-full text-[9px] font-bold uppercase tracking-widest border ${selectedCreator.status === 'active' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-red-50 text-red-600 border-red-100'}`}>
                                        {selectedCreator.status}
                                    </span>
                                </div>
                            </div>

                            {/* Payout Info */}
                            <div className="bg-zinc-50 p-4 rounded-lg border border-zinc-100">
                                <div className="flex items-center space-x-2 text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">
                                    <DollarSign size={12} className="text-emerald-500" />
                                    <span>Payout Method</span>
                                </div>
                                {selectedCreator.paypalEmail ? (
                                    <div className="text-sm font-mono font-bold text-zinc-800 break-all bg-white p-2 border border-zinc-200 rounded">
                                        {selectedCreator.paypalEmail}
                                    </div>
                                ) : (
                                    <div className="text-xs italic text-gray-400">No PayPal email provided</div>
                                )}
                            </div>

                            {/* Referral Stats */}
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-2 text-[10px] font-bold uppercase tracking-widest text-gray-400">
                                        <Users size={12} className="text-blue-500" />
                                        <span>Referral Tracking</span>
                                    </div>
                                    <div className="text-[10px] font-mono text-zinc-400">
                                        Code: <span className="text-zinc-800 font-bold">{selectedCreator.referralLink || selectedCreator.slug?.current || 'None'}</span>
                                    </div>
                                </div>

                                {loadingReferrals ? (
                                    <div className="text-center py-8">
                                        <div className="text-[10px] font-mono text-gray-400 animate-pulse">Querying Stripe...</div>
                                    </div>
                                ) : referrals ? (
                                    <div className="space-y-3">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="bg-blue-50 p-3 rounded-lg border border-blue-100">
                                                <div className="text-2xl font-bold text-blue-700">{referrals.count}</div>
                                                <div className="text-[8px] font-bold uppercase tracking-widest text-blue-400">Total Referrals</div>
                                            </div>
                                            <div className="bg-emerald-50 p-3 rounded-lg border border-emerald-100">
                                                <div className="text-2xl font-bold text-emerald-700">
                                                    ${(referrals.totalRevenue || 0).toFixed(2)}
                                                </div>
                                                <div className="text-[8px] font-bold uppercase tracking-widest text-emerald-400">Pushed Revenue</div>
                                            </div>
                                        </div>

                                        {referrals.referrals.length > 0 ? (
                                            <div className="max-h-[200px] overflow-y-auto pr-2 custom-scrollbar">
                                                <table className="w-full text-[10px]">
                                                    <thead className="sticky top-0 bg-white border-b border-gray-100 text-gray-400 font-bold uppercase tracking-tighter">
                                                        <tr>
                                                            <th className="py-2 text-left">Customer</th>
                                                            <th className="py-2 text-center">Tier</th>
                                                            <th className="py-2 text-right">Value</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-gray-50">
                                                        {referrals.referrals.map((ref, i) => (
                                                            <tr key={i}>
                                                                <td className="py-2 text-gray-600 truncate max-w-[120px]" title={ref.email}>{ref.email}</td>
                                                                <td className="py-2 text-center">
                                                                    <span className="px-1.5 py-0.5 rounded bg-gray-100 font-bold uppercase text-[7px] text-gray-500">
                                                                        {ref.tier}
                                                                    </span>
                                                                </td>
                                                                <td className="py-2 text-right font-mono text-emerald-600 font-bold">
                                                                    ${ref.lifetimeValue?.toFixed(2)}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        ) : (
                                            <div className="text-center py-4 bg-gray-50 rounded border border-dashed border-gray-200 text-[10px] text-gray-400 italic">
                                                No tracked signups yet.
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="text-center py-4 bg-gray-50 rounded border border-dashed border-gray-200 text-[10px] text-gray-400 italic">
                                        Failed to load referrals
                                    </div>
                                )}
                            </div>

                            {/* Danger Zone */}
                            <div className="pt-4 border-t border-gray-100">
                                <button
                                    onClick={() => handleDelete(selectedCreator._id, selectedCreator.name)}
                                    className="w-full py-3 bg-red-50 text-red-600 rounded flex items-center justify-center space-x-2 text-[10px] font-bold uppercase tracking-widest hover:bg-red-100 transition-colors"
                                >
                                    <Trash2 size={14} />
                                    <span>Terminate Account</span>
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {error && (
                <div className="mt-4 p-4 bg-red-50 text-red-600 rounded border border-red-100 text-xs font-mono">
                    <p className="font-bold uppercase tracking-widest mb-1">Error:</p>
                    <p>{error}</p>
                </div>
            )}
        </div>
    );
};

export default AdminCreators;
